import { handlePromise } from "./handlePromisse"
import { getHtmlByConsultationId, getPdfByConsultationId, getPdf, getHtml } from "../services/decisionFlow"
import { QueryModel } from "../pages/main/query/query.model"
import { ProductModel } from "../models/Product.model"
import { oralPDF, oralHTML } from "../services/oralService"
import { PdfModel } from "../models/Pdf.model"
import { FlowType } from "../pages/main/query/flow-type";
import html2pdf from 'html2pdf.js';
import { isNewPdfVersionEnabled } from "../services/feature-flags.service";

export const redirectToPDF = (base64: string) => {
    const bin = atob(base64.replace(/\s/g, ''))
    const buffer = new ArrayBuffer(bin.length)
    const view = new Uint8Array(buffer)
    for (let i = 0; i < bin.length; i++) {
        view[i] = bin.charCodeAt(i)
    }
    const blob = new Blob([view], { type: 'application/pdf' })

    const linkEl = document.createElement('a')
    linkEl.download = `${new Date().getTime()}.pdf`
    linkEl.target = '_blank'
    linkEl.href = URL.createObjectURL(blob)
    document.body.appendChild(linkEl)
    setTimeout(() => {
        linkEl.click()
        document.body.removeChild(linkEl)
    }, 500)
}

export const downloadPdfFromHtml = async (html: string): Promise<void> => {
    return new Promise((resolve, reject) => {
        const iframe = document.createElement('iframe')
        iframe.style.position = 'fixed'
        iframe.style.top = '0'
        iframe.style.left = '0'
        iframe.style.width = '794px' // A4 width at 96dpi (210mm)
        iframe.style.height = '1123px' // A4 height at 96dpi (297mm)
        iframe.style.border = 'none'
        iframe.style.opacity = '0'
        iframe.style.pointerEvents = 'none'
        iframe.style.zIndex = '-1'
        document.body.appendChild(iframe)

        const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document
        if (!iframeDoc) {
            document.body.removeChild(iframe)
            reject(new Error('Could not access iframe document'))
            return
        }

        iframeDoc.open()
        iframeDoc.write(html)
        iframeDoc.close()

        // Wait for fonts and images to load
        setTimeout(async () => {
            try {
                const element = iframeDoc.body

                const options = {
                    margin: [0.3, 0.3, 0.3, 0.3] as [number, number, number, number],
                    filename: `formula-${new Date().getTime()}.pdf`,
                    image: { type: 'jpeg' as const, quality: 0.98 },
                    html2canvas: {
                        scale: 2,
                        useCORS: true,
                        allowTaint: true,
                        logging: false,
                        windowWidth: 794,
                        windowHeight: 1123
                    },
                    jsPDF: {
                        unit: 'in' as const,
                        format: 'a4' as const,
                        orientation: 'portrait' as const
                    }
                }

                await html2pdf().set(options).from(element).save()
                resolve()
            } catch (error) {
                reject(error)
            } finally {
                document.body.removeChild(iframe)
            }
        }, 1500) // Wait for fonts and images to load
    })
}

export const openHtmlForPrint = (html: string) => {
    downloadPdfFromHtml(html)
}

export const generatePdf = async (consultationId: number, type: FlowType, setLoading: Function, setSnackbarMessage: Function, setShowSnackbar: Function) => {
    setLoading(true)
    const [result, error] = await handlePromise(getPdfByConsultationId({
        consults: [{ consultationId, type }]
    }, false))

    if (error) {
        setSnackbarMessage(error.response.data.message)
        setShowSnackbar(true)
        setLoading(false)
        return
    }

    const base64 = result.data.pdf
    redirectToPDF(base64)
    setLoading(false)
}

export const generateHtml = async (consultationId: number, type: FlowType, setLoading: Function, setSnackbarMessage: Function, setShowSnackbar: Function) => {
    setLoading(true)
    const [result, error] = await handlePromise(getHtmlByConsultationId({
        consults: [{ consultationId, type }]
    }))

    if (error) {
        setSnackbarMessage(error.response?.data?.message || 'Erro ao gerar documento')
        setShowSnackbar(true)
        setLoading(false)
        return
    }

    const html = result.data.html
    openHtmlForPrint(html)
    setLoading(false)
}

/**
 * Gera PDF usando a estratégia definida pela feature flag:
 * - Flag OFF: Backend gera PDF com Puppeteer (retorna base64)
 * - Flag ON: Backend retorna HTML, frontend gera PDF com html2pdf.js
 */
export const generatePdfWithFeatureFlag = async (
    consultationId: number,
    type: FlowType,
    setLoading: Function,
    setSnackbarMessage: Function,
    setShowSnackbar: Function
) => {
    if (isNewPdfVersionEnabled()) {
        // V3: Frontend gera o PDF a partir do HTML
        await generateHtml(consultationId, type, setLoading, setSnackbarMessage, setShowSnackbar)
    } else {
        // V2: Backend gera o PDF com Puppeteer
        await generatePdf(consultationId, type, setLoading, setSnackbarMessage, setShowSnackbar)
    }
}

export type ConsultInput = { consultationId: number, type: string }

/**
 * Gera PDF para múltiplas consultas usando a estratégia da feature flag.
 * Usado principalmente na página de histórico.
 * @returns { success: boolean, html?: string, error?: string, hasConflict?: boolean }
 */
export const generatePdfForConsultations = async (
    consults: ConsultInput[]
): Promise<{ success: boolean, html?: string, error?: string, hasConflict?: boolean, message?: string }> => {
    if (isNewPdfVersionEnabled()) {
        // V3: Backend retorna HTML, frontend gera PDF
        const [result, error] = await handlePromise(getHtmlByConsultationId({ consults }))
        if (error) {
            if (error.response?.data?.error === 'HAS_CONFLICT') {
                return { success: false, hasConflict: true, message: error.response.data.message }
            }
            return { success: false, error: error.response?.data?.message || 'Erro ao gerar documento' }
        }
        return { success: true, html: result.data.html }
    } else {
        // V2: Backend gera PDF com Puppeteer (retorna base64)
        const [result, error] = await handlePromise(getPdfByConsultationId({ consults }, false))
        if (error) {
            if (error.response?.data?.error === 'HAS_CONFLICT') {
                return { success: false, hasConflict: true, message: error.response.data.message }
            }
            return { success: false, error: error.response?.data?.message || 'Erro ao gerar documento' }
        }
        redirectToPDF(result.data.pdf)
        return { success: true }
    }
}

/**
 * Gera PDF sem paciente (body endpoint) usando a estratégia da feature flag.
 * @returns { success: boolean, html?: string, error?: string }
 */
export const generatePdfForBody = async (
    model: QueryModel,
    userId: number,
    observation: string,
    observations: string[],
    products: ProductModel[],
    consultName?: string
): Promise<{ success: boolean, html?: string, error?: string }> => {
    if (isNewPdfVersionEnabled()) {
        // V3: Backend retorna HTML, frontend gera PDF
        const [result, error] = await handlePromise(getHtml(model, userId, observation, observations, products, consultName))
        if (error) {
            return { success: false, error: error.response?.data?.message || 'Erro ao gerar documento' }
        }
        return { success: true, html: result.data.html }
    } else {
        // V2: Backend gera PDF com Puppeteer (retorna base64)
        const [result, error] = await handlePromise(getPdf(model, userId, observation, observations, products, consultName))
        if (error) {
            return { success: false, error: error.response?.data?.message || 'Erro ao gerar documento' }
        }
        redirectToPDF(result.data.pdf)
        return { success: true }
    }
}

/**
 * Gera PDF oral usando a estratégia da feature flag.
 * @returns { success: boolean, html?: string, error?: string }
 */
export const generatePdfOral = async (
    pdfData: PdfModel
): Promise<{ success: boolean, html?: string, error?: string }> => {
    if (isNewPdfVersionEnabled()) {
        // V3: Backend retorna HTML, frontend gera PDF
        const [result, error] = await handlePromise(oralHTML(pdfData))
        if (error) {
            return { success: false, error: error.response?.data?.message || 'Erro ao gerar documento' }
        }
        return { success: true, html: result.data.html }
    } else {
        // V2: Backend gera PDF com Puppeteer (retorna base64)
        const [result, error] = await handlePromise(oralPDF(pdfData))
        if (error) {
            return { success: false, error: error.response?.data?.message || 'Erro ao gerar documento' }
        }
        redirectToPDF(result.data.pdf)
        return { success: true }
    }
}
